import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { LOGO_COLOR } from '@/lib/logos';
import LanguageSelector from '@/components/common/LanguageSelector';
import { useI18n } from '@/lib/I18nContext';

const NAV_LINKS = [
  { labelKey: 'landing.nav_how_it_works', href: '/como-funciona' },
  { labelKey: 'landing.nav_integrations', href: '/integraciones' },
  { labelKey: 'landing.nav_use_cases', href: '/casos-de-uso' },
  { labelKey: 'landing.nav_contact', href: '/contacto' },
];

export default function PublicNavbar() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (href) => location.pathname === href;

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled || mobileOpen ? 'rgba(15,23,41,0.97)' : 'transparent',
          backdropFilter: scrolled || mobileOpen ? 'blur(12px)' : 'none',
          borderBottom: scrolled || mobileOpen ? '1px solid rgba(255,255,255,0.08)' : 'none',
        }}
      >
        <div
          className="max-w-6xl mx-auto flex items-center"
          style={{ paddingLeft: 32, paddingRight: 32, height: 72, gap: 20 }}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src={LOGO_COLOR}
              alt="Bezkraen"
              className="w-auto object-contain"
              style={{ height: 'clamp(22px, 2.5vw, 32px)' }}
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-6 flex-1">
            {NAV_LINKS.map(({ labelKey, href }) => (
              <Link
                key={href}
                to={href}
                className="transition-colors relative group whitespace-nowrap"
                style={{ color: isActive(href) ? '#ffffff' : 'rgba(255,255,255,0.7)', fontSize: 14, letterSpacing: '0.01em' }}
              >
                {t(labelKey)}
                <span
                  className="absolute -bottom-0.5 left-0 h-px bg-white transition-all duration-300"
                  style={{ width: isActive(href) ? '100%' : '0', background: isActive(href) ? '#6B8CFF' : 'white' }}
                />
              </Link>
            ))}
          </div>

          {/* CTA buttons + mobile toggle */}
          <div className="flex items-center gap-3 shrink-0 ml-auto">
            <div className="mr-1 hidden lg:block"><LanguageSelector variant="dark" /></div>
            <button
              onClick={() => base44.auth.redirectToLogin()}
              className="hidden lg:flex items-center font-medium text-white/80 hover:text-white px-4 rounded-lg border border-white/20 hover:border-white/40 transition-all whitespace-nowrap"
              style={{ fontSize: 14, height: 40 }}
            >
              {t('landing.nav_login')}
            </button>
            <button
              onClick={() => base44.auth.redirectToLogin()}
              className="hidden lg:flex items-center font-semibold text-white px-5 rounded-lg transition-all hover:scale-[1.03] active:scale-[0.98] whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #22D3FF 0%, #00D5FF 60%, #33C7FF 100%)', color: '#070B14', fontSize: 14, height: 40, boxShadow: '0 4px 18px -4px rgba(0,213,255,0.5)' }}
            >
              {t('landing.nav_start_free')}
            </button>
            <button
              className="lg:hidden p-2 text-white/80 hover:text-white"
              onClick={() => setMobileOpen(v => !v)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10 px-6 py-4 space-y-1">
            {NAV_LINKS.map(({ labelKey, href }) => (
              <Link
                key={href}
                to={href}
                className="flex items-center justify-between py-3 border-b border-white/10 text-sm transition-colors"
                style={{ color: isActive(href) ? '#6B8CFF' : 'rgba(255,255,255,0.75)' }}
              >
                <span>{t(labelKey)}</span>
                {isActive(href) && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
              </Link>
            ))}
            <div className="pt-3">
              <button
                onClick={() => base44.auth.redirectToLogin()}
                className="w-full text-center text-sm font-medium text-white/80 py-2.5 rounded-lg border border-white/20 mb-2"
              >
                {t('landing.nav_login')}
              </button>
              <button
                onClick={() => base44.auth.redirectToLogin()}
                className="w-full text-center text-sm font-semibold text-white py-3 rounded-lg"
                style={{ background: 'linear-gradient(135deg, #22D3FF 0%, #00D5FF 60%, #33C7FF 100%)', color: '#070B14', boxShadow: '0 4px 18px -4px rgba(0,213,255,0.5)' }}
              >
                {t('landing.nav_start_free')}
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}